import { useEffect, useState, useMemo } from "react";
import { MaterialReactTable } from "material-react-table";
import { useDispatch, useSelector } from "react-redux";
import { CircularProgress, Chip } from "@mui/material";
import {
  getAdminDashboardCredentials,
  getAdminDashboardInvoiceCountByCompanies,
} from "../../Actions/AdminDashboardAction";
import { getCompany } from "../../Actions/CompanyAction";
import { globalStyle } from "../../global/Utils";
import { StatCard } from "./StatCard";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { useNavigate } from "react-router-dom";

const Index = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [paginationForInvoice, setPaginationForInvoice] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [paginationForCompanies, setPaginationForCompanies] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const isGettingCompanyList = useSelector(
    (state) => state.CompanyListReducer?.isGettingCompanyList,
  );
  const companiesList = useSelector(
    (state) => state.CompanyListReducer?.companiesList || [],
  );
  const totalResults = useSelector(
    (state) => state.CompanyListReducer?.totalResults || 0,
  );
  const {
    isGettingAdminDashboardCredentials,
    adminDashboardCredentials,
    adminDashboardInvoiceCountByCompanies,
    isGettingAdminDashboardInvoiceCountByCompanies,
    totalResultsForInvoice,
  } = useSelector((state) => state?.AdminDashboardReducer);

  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(getAdminDashboardCredentials());
    }, 500);
    return () => clearTimeout(timer);
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      getAdminDashboardInvoiceCountByCompanies(
        paginationForInvoice.pageIndex,
        paginationForInvoice.pageSize,
      ),
    );
  }, [paginationForInvoice.pageIndex, paginationForInvoice.pageSize, dispatch]);

  useEffect(() => {
    dispatch(
      getCompany(
        paginationForCompanies.pageIndex,
        paginationForCompanies.pageSize,
        "",
      ),
    );
  }, [
    paginationForCompanies.pageIndex,
    paginationForCompanies.pageSize,
    dispatch,
  ]);

  const columnsForInvoiceTable = useMemo(
    () => [
      {
        accessorKey: "_id",
        header: "#",
        size: 40,
        enableResizing: false,
        enableEditing: false,
        Cell: ({ row }) =>
          row.index +
          1 +
          paginationForInvoice.pageIndex * paginationForInvoice.pageSize,
      },
      {
        accessorKey: "companyName",
        header: "Company Name",
        grow: 1,
      },
      {
        accessorKey: "totalInvoices",
        header: "Total Invoices",
        grow: 1,
      },
    ],
    [paginationForInvoice.pageIndex, paginationForInvoice.pageSize],
  );

  const columnsForCompaniesList = useMemo(
    () => [
      {
        accessorKey: "companyName",
        header: "Company Name",
        size: 180,
      },
      {
        accessorKey: "companyStatus",
        header: "Company Status",
        size: 120,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          let status = "";
          let color = "default";
          let bgColor = "#e0e0e0";
          if (value === "Sandbox") {
            status = "SB";
            bgColor = "#ff9800";
            color = "#fff";
          } else if (value === "Production") {
            status = "P";
            bgColor = "#4caf50";
            color = "#fff";
          }
          return (
            <Chip
              label={status || "-"}
              size="small"
              sx={{
                backgroundColor: bgColor,
                color: color,
                fontWeight: 500,
              }}
            />
          );
        },
      },
      {
        id: "tokens",
        header: "Tokens",
        size: 150,
        Cell: ({ row }) => {
          const sandbox = row.original?.sandBoxToken;
          const production = row.original?.productiontoken;

          const isSandboxValid =
            typeof sandbox === "string" && sandbox.length === 36;

          const isProductionValid =
            typeof production === "string" && production.length === 36;

          return (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "4px" }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "6px" }}
              >
                <span style={{ fontSize: 12 }}>Sandbox:</span>
                {isSandboxValid ? (
                  <CheckCircleIcon sx={{ color: "green", fontSize: 18 }} />
                ) : (
                  <CancelIcon sx={{ color: "red", fontSize: 18 }} />
                )}
              </div>

              <div
                style={{ display: "flex", alignItems: "center", gap: "6px" }}
              >
                <span style={{ fontSize: 12 }}>Production:</span>
                {isProductionValid ? (
                  <CheckCircleIcon sx={{ color: "green", fontSize: 18 }} />
                ) : (
                  <CancelIcon sx={{ color: "red", fontSize: 18 }} />
                )}
              </div>
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line
    [paginationForCompanies.pageIndex, paginationForCompanies.pageSize],
  );

  // Loader
  if (
    isGettingAdminDashboardCredentials ||
    isGettingAdminDashboardInvoiceCountByCompanies ||
    isGettingCompanyList ||
    !adminDashboardCredentials
  ) {
    return (
      <div
        className="flex items-center justify-center h-screen"
        style={{ background: globalStyle.bgColor }}
      >
        <CircularProgress />
      </div>
    );
  }

  const {
    totalUsers,
    totalActiveCustomer,
    totalActiveAdmin,
    totalCompanies,
    totalInvoices,
  } = adminDashboardCredentials;

  return (
    <div
      className="min-h-screen p-6"
      style={{ background: globalStyle.bgColor }}
    >
      {/* Header */}
      <div className="mb-6">
        <h1
          className="text-3xl font-bold"
          style={{
            fontFamily: globalStyle.fontFamilyHeading,
            color: globalStyle.bgColorBlue,
          }}
        >
          Admin Dashboard
        </h1>
        <p
          className="text-sm mt-1"
          style={{ fontFamily: globalStyle.fontFamilyParagraph }}
        >
          Overview of system statistics
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
        <StatCard
          title="Total Users"
          value={totalUsers}
          icon="👥"
          Navigate={() => navigate("/main/view-user-list")}
        />
        <StatCard
          title="Active Customers"
          value={totalActiveCustomer}
          icon="🧑‍💼"
          Navigate={() => navigate("/main/view-user-list", {state: { statusFromDashboard: "customer" }})}
        />
        <StatCard
          title="Active Admins"
          value={totalActiveAdmin}
          icon="🛡️"
          Navigate={() => navigate("/main/view-user-list", {state: { statusFromDashboard: "admin" }})}
        />
        <StatCard
          title="Total Companies"
          value={totalCompanies}
          icon="🏢"
          Navigate={() => navigate("/main/view-company-list")}
        />
        <StatCard
          title="Total Invoices"
          value={totalInvoices}
          icon="📄"
          Navigate={() => navigate("/main/view-invoice-list")}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {/* Invoices by Company */}
        <div
          className="rounded-xl shadow-xl p-5"
          style={{ background: globalStyle.bgColorWhite }}
        >
          <h2
            className="text-lg font-semibold mb-4"
            style={{ fontFamily: globalStyle.fontFamilyHeading }}
          >
            Invoices by Company
          </h2>
          <MaterialReactTable
            columns={columnsForInvoiceTable}
            data={adminDashboardInvoiceCountByCompanies || []}
            enableRowActions={false}
            manualPagination
            rowCount={totalResultsForInvoice}
            state={{
              columnOrder: ["_id", "companyName", "totalInvoices"],
              isLoading: isGettingAdminDashboardInvoiceCountByCompanies,
              pagination: paginationForInvoice,
            }}
            enableGlobalFilter={false}
            onPaginationChange={setPaginationForInvoice}
            columnFilterDisplayMode={false}
            muiTablePaperProps={{
              sx: {
                backgroundColor: globalStyle.bgColorWhite,
                borderRadius: `${globalStyle.borderRadius}px`,
                fontFamily: globalStyle.fontFamily,
              },
            }}
            muiTableHeadCellProps={{
              sx: {
                backgroundColor: globalStyle.blueColor,
                color: "#fff",
                fontSize: globalStyle.fontSize,
                fontFamily: globalStyle.fontFamilyHeading,
                fontWeight: 600,
                borderBottom: "none",
                whiteSpace: "normal",
                wordBreak: "break-word",
              },
            }}
            muiTableBodyCellProps={{
              sx: {
                fontSize: globalStyle.fontSize,
                fontFamily: globalStyle.fontFamilyParagraph,
                color: "#333",
                borderBottom: `1px solid ${globalStyle.curveLightColor}`,
                whiteSpace: "normal",
                wordBreak: "break-word",
              },
            }}
            muiTableBodyRowProps={{
              sx: {
                backgroundColor: globalStyle.bgColorWhite,
                "&:hover": {
                  backgroundColor: globalStyle.bgColor,
                },
              },
            }}
            muiTableContainerProps={{
              sx: {
                maxHeight: "none", // ✅ remove height restriction
                overflow: "visible", // ✅ allow content to expand
                borderRadius: `${globalStyle.borderRadius}px`,
              },
            }}
            muiPaginationProps={{
              sx: {
                "& .MuiTablePagination-toolbar": {
                  fontFamily: globalStyle.fontFamily,
                },
                "& .Mui-selected": {
                  backgroundColor: globalStyle.blueColor,
                  color: "#fff",
                },
              },
            }}
            muiTableProps={{
              sx: {
                tableLayout: "fixed",
              },
            }}
          />
        </div>
        {/* Companies Status */}
        <div
          className="rounded-xl shadow-xl p-5"
          style={{ background: globalStyle.bgColorWhite }}
        >
          <h2
            className="text-lg font-semibold mb-4"
            style={{ fontFamily: globalStyle.fontFamilyHeading }}
          >
            Companies
          </h2>
          <MaterialReactTable
            columns={columnsForCompaniesList}
            data={companiesList || []}
            enableRowActions={false}
            manualPagination
            rowCount={totalResults}
            state={{
              columnOrder: ["companyName", "companyStatus", "tokens"],
              isLoading: isGettingCompanyList,
              pagination: paginationForCompanies,
            }}
            enableGlobalFilter={false}
            onPaginationChange={setPaginationForCompanies}
            columnFilterDisplayMode={false}
            enableColumnResizing
            columnResizeMode="onChange"
            muiTablePaperProps={{
              sx: {
                backgroundColor: globalStyle.bgColorWhite,
                borderRadius: `${globalStyle.borderRadius}px`,
                fontFamily: globalStyle.fontFamily,
              },
            }}
            muiTableHeadCellProps={{
              sx: {
                backgroundColor: globalStyle.blueColor,
                color: "#fff",
                fontSize: globalStyle.fontSize,
                fontFamily: globalStyle.fontFamilyHeading,
                fontWeight: 600,
                borderBottom: "none",
                whiteSpace: "normal",
                wordBreak: "break-word",
              },
            }}
            muiTableBodyCellProps={{
              sx: {
                fontSize: globalStyle.fontSize,
                fontFamily: globalStyle.fontFamilyParagraph,
                color: "#333",
                borderBottom: `1px solid ${globalStyle.curveLightColor}`,
                whiteSpace: "normal",
                wordBreak: "break-word",
              },
            }}
            muiTableBodyRowProps={{
              sx: {
                backgroundColor: globalStyle.bgColorWhite,
                "&:hover": {
                  backgroundColor: globalStyle.bgColor,
                },
              },
            }}
            muiTableContainerProps={{
              sx: {
                maxHeight: "none", // ✅ remove height restriction
                overflow: "visible", // ✅ allow content to expand
                borderRadius: `${globalStyle.borderRadius}px`,
              },
            }}
            muiPaginationProps={{
              sx: {
                "& .MuiTablePagination-toolbar": {
                  fontFamily: globalStyle.fontFamily,
                },
                "& .Mui-selected": {
                  backgroundColor: globalStyle.blueColor,
                  color: "#fff",
                },
              },
            }}
            muiTableProps={{
              sx: {
                tableLayout: "fixed",
              },
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default Index;
